'use client'

import { Card, CardContent } from '@/components/card'
import { Checkbox } from '@/components/checkbox'
import { Button } from '@/components/ui/common/Button'
import { cn } from '@/utils/common'
import * as React from 'react'

interface Choice {
  id: string
  label: string
}

interface Question {
  id: string
  text: string
  choices: Choice[]
  maxSelections?: number
  requiredSelections?: number
  timeLimit: number
}

interface MultipleChoiceQuestionCardProps {
  question: Question
  selectedAnswers: string[]
  onAnswerChange: (selectedIds: string[]) => void
  onNext?: () => void
  className?: string
}

export function MultipleChoiceQuestionCard({
  question,
  selectedAnswers,
  onAnswerChange,
  onNext,
  className
}: MultipleChoiceQuestionCardProps) {
  const [remainingTime, setRemainingTime] = React.useState<number | null>(question.timeLimit)

  React.useEffect(() => {
    setRemainingTime(question.timeLimit)
  }, [question.timeLimit])

  React.useEffect(() => {
    if (!question.timeLimit) return

    const timer = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev === null || prev <= 0) {
          clearInterval(timer)
          onNext?.()
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [question.timeLimit, onNext])

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  const handleAnswerChange = (answerId: string) => {
    const newSelectedAnswers = selectedAnswers.includes(answerId)
      ? selectedAnswers.filter((id) => id !== answerId)
      : [...selectedAnswers, answerId]

    if (question.maxSelections && newSelectedAnswers.length > question.maxSelections) {
      return
    }

    onAnswerChange(newSelectedAnswers)
  }

  return (
    <div className="relative">
      {remainingTime !== null && (
        <div className="absolute -top-8 right-0 text-sm font-medium text-gray-600 bg-transparent px-3 py-1 rounded-md">
          Pozostały czas: {formatTime(remainingTime)}
        </div>
      )}
      <Card className={cn('w-full max-w-2xl lg:min-w-md rounded-xl', className)}>
        <CardContent className="p-6">
          <div className="flex w-full justify-center">
            <h2 className="mb-4 text-lg font-semibold">{question.text}</h2>
          </div>
          <div className="space-y-2">
            {question.choices.map((choice) => (
              <div
                className={cn(
                  'flex items-center space-x-2 rounded-md border p-3 transition-colors',
                  selectedAnswers.includes(choice.id)
                    ? 'border-[#2bcaca] bg-[#2bcaca]/10'
                    : 'border-gray-200'
                )}
                key={choice.id}
              >
                <Checkbox
                  checked={selectedAnswers.includes(choice.id)}
                  id={choice.id}
                  onCheckedChange={() => handleAnswerChange(choice.id)}
                />
                <label
                  className="text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  htmlFor={choice.id}
                >
                  {choice.label}
                </label>
              </div>
            ))}
          </div>
          {question.requiredSelections && (
            <div className="mt-2 text-center text-sm text-gray-500">
              Wybrano {selectedAnswers.length} z {question.requiredSelections} wymaganych
              odpowiedzi
            </div>
          )}
          {onNext && (
            <div className="mt-4 flex justify-center">
              <Button
                className="bg-[#2bcaca] hover:bg-[#2b9999]"
                disabled={selectedAnswers.length === 0}
                onClick={onNext}
              >
                Dalej
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
